// Adding a starter, another main dish or a dessert to a meal: a recipe or a free text, with optional servings.
import React, { useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, List, ListItemButton, ListItemText, Paper,
  TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import * as T from '../types';
import { mealCourseText, parseNumber, searchKey } from '../labels';
import { recipeSubtitle } from './shared';

const MAX_RESULTS = 8;

/** Recipes offered first for each course. */
const RECIPE_COURSE: Record<T.MealCourse, T.RecipeCourse> = { Starter: 'Starter', Main: 'Main', Dessert: 'Dessert' };

/** Mount it when opening. `onClose(null)` = cancelled. */
export function DishDialog({ course, recipes, onClose }: {
  course: T.MealCourse; recipes: T.RecipeSummary[]; onClose: (dish: T.MealDish | null) => void;
}) {
  const [search, setSearch] = useState('');
  const [servingsText, setServingsText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const key = searchKey(search);
  const results = (key.length > 0
    ? recipes.filter(r => searchKey(r.name).includes(key))
    : recipes.filter(r => r.course === RECIPE_COURSE[course]))
    .slice(0, MAX_RESULTS);

  const finish = (recipe: T.RecipeSummary | null) => {
    let servingsOverride: number | null = null;
    if (servingsText.trim()) {
      const value = parseNumber(servingsText);
      if (value === null || value <= 0) return setError('As doses devem ser um número positivo, ou fica em branco para todos.');
      servingsOverride = value;
    }
    const text = search.trim();
    onClose({
      course,
      recipePublicId: recipe?.publicId ?? null,
      name: recipe?.name ?? text,
      freeText: recipe ? null : text,
      servingsOverride,
      servings: servingsOverride ?? 0,
    });
  };

  const emptyText = key.length > 0
    ? 'Nenhuma receita com esse nome.'
    : course === 'Starter' ? 'Ainda não há receitas do tipo «Entrada». Procura outra receita ou escreve o nome.'
    : course === 'Dessert' ? 'Ainda não há receitas do tipo «Sobremesa». Procura outra receita ou escreve o nome.'
    : 'Ainda não há receitas.';

  return (
    <Dialog open onClose={() => onClose(null)} maxWidth="xs" fullWidth>
      <DialogTitle>{mealCourseText(course)}</DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth size="small" autoFocus placeholder="Procurar receita ou escrever o nome" value={search} onChange={e => setSearch(e.target.value)}
          slotProps={{
            input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> },
            htmlInput: { maxLength: 100 },
          }}
        />
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1, mb: 0.5 }}>
          {key.length > 0 ? 'Resultados' : 'Receitas'}
        </Typography>
        {results.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>{emptyText}</Typography>
        ) : (
          <Paper variant="outlined">
            <List dense disablePadding>
              {results.map(r => (
                <ListItemButton key={r.publicId} onClick={() => finish(r)}>
                  <ListItemText primary={r.name} secondary={recipeSubtitle(r)} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}
        {search.trim() && (
          <Button sx={{ mt: 1 }} onClick={() => finish(null)}>Usar «{search.trim()}» sem receita</Button>
        )}
        <TextField
          fullWidth size="small" sx={{ mt: 2 }} label="Doses" placeholder="Para todos" value={servingsText}
          onChange={e => setServingsText(e.target.value)} helperText="Em branco: para todos os que comem esta refeição."
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
        />
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(null)}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
}
